/* Fires a custom usage-analytics event to the self-hosted Umami tracker.
 * Phase 4.5 of docs/plans/todo/self-hosted-usage-analytics.md ("button tags").
 *
 * Deliberate deviation from the plan's literal "data-umami-event" wording: we
 * fire imperatively through this guarded helper instead of the declarative DOM
 * attribute. Reasons — (1) it reuses the blessed webVitalsToUmami.ts pattern
 * (repo precedent) rather than introducing an untested one; (2) the declarative
 * auto-tag path is NOT proven to honor the opt-out, whereas this guard makes
 * compliance correct by construction; (3) it needs zero prop-spreading refactors
 * on the app's many custom Button components.
 *
 * Behaviour, mirroring webVitalsToUmami.ts:
 * - Rides the tracker injected by public/app-boot.js. When the tracker is absent
 *   (local dev / CI — no REACT_APP_UMAMI_* config), umami is undefined -> no-op,
 *   no requests.
 * - umami.track() does NOT honor localStorage['umami.disabled']
 *   (umami-software/umami#3031), so the opt-out is enforced here explicitly.
 * - The tracker's before-send normalizer scrubs the page URL attached to the
 *   event (D-Norm), so no resource IDs leak via the URL.
 *
 * GUARDRAIL (CNIL, load-bearing): NEVER pass user / org / project identifiers in
 * `name` or `data`. The before-send scrubber touches url/referrer/title only —
 * it does NOT scrub event names or event data. Keep these strictly aggregate.
 */

type UmamiWindow = Window & {
  umami?: { track?: (event: string, data?: Record<string, unknown>) => void };
};

export const trackEvent = (name: string, data?: Record<string, unknown>): void => {
  if (localStorage.getItem("umami.disabled") === "1") return;
  (window as UmamiWindow).umami?.track?.(name, data);
};

export default trackEvent;
